/** Customer list and search (§4.3). */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Building2, FileUp, Plus, Search } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { CustomerImportModal } from './CustomerImportModal';
import {
  Button,
  Card,
  EmptyState,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

interface CustomerRow {
  id: string;
  code: string;
  name: string;
  tin: string | null;
  city: string | null;
  province: string | null;
  isGovernment: boolean;
  status: 'ACTIVE' | 'DISABLED';
  category: { label: string } | null;
  industry: { label: string } | null;
  contacts: { name: string; mobile: string | null }[];
  _count: { leads: number; salesOrders: number };
}

export function CustomersPage(): JSX.Element {
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | 'ACTIVE' | 'DISABLED'>('ACTIVE');
  const [page, setPage] = useState(1);
  const [importing, setImporting] = useState(false);

  const customers = useQuery({
    queryKey: ['customers', search, status, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: '25' });
      if (search) params.set('q', search);
      if (status) params.set('status', status);
      return api<{ data: CustomerRow[]; total: number; pageSize: number }>(
        `/customers?${params.toString()}`,
      );
    },
  });

  const total = customers.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / (customers.data?.pageSize ?? 25)));

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">Customers</h1>
          <p className="text-sm text-slate-500">
            Everyone we sell to. Disabled customers keep their history but cannot take new leads.
          </p>
        </div>
        {can('customers.create') && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setImporting(true)}>
              <FileUp aria-hidden="true" className="h-4 w-4" /> Import
            </Button>
            <Link to="/customers/new">
              <Button>
                <Plus aria-hidden="true" className="h-4 w-4" /> Add customer
              </Button>
            </Link>
          </div>
        )}
      </header>
      {importing && <CustomerImportModal onClose={() => setImporting(false)} />}

      <Card>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="relative min-w-64 flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              className={`${inputClass} pl-9`}
              placeholder="Search name, TIN, city or contact"
              value={search}
              onChange={(event) => {
                setPage(1);
                setSearch(event.target.value);
              }}
            />
          </div>
          <select
            className={`${inputClass} w-40`}
            value={status}
            onChange={(event) => {
              setPage(1);
              setStatus(event.target.value as typeof status);
            }}
          >
            <option value="ACTIVE">Active only</option>
            <option value="DISABLED">Disabled only</option>
            <option value="">All</option>
          </select>
        </div>

        {customers.isLoading && <Skeleton className="h-64 w-full" />}

        {customers.data?.data.length === 0 && (
          <EmptyState
            title={search ? 'No customers match that search.' : 'No customers yet'}
            description={search ? undefined : 'Add the first customer to start raising leads.'}
            action={
              can('customers.create') && !search ? (
                <Link to="/customers/new">
                  <Button>
                    <Plus aria-hidden="true" className="h-4 w-4" /> Add customer
                  </Button>
                </Link>
              ) : undefined
            }
          />
        )}

        {(customers.data?.data.length ?? 0) > 0 && (
          <>
            <TableWrap>
              <thead>
                <tr>
                  <Th>Customer</Th>
                  <Th>Category</Th>
                  <Th>Location</Th>
                  <Th>Primary contact</Th>
                  <Th align="right">Leads</Th>
                  <Th align="right">Status</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {customers.data?.data.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <Td>
                      <Link
                        to={`/customers/${row.id}`}
                        className="font-medium text-navy hover:underline dark:text-white"
                      >
                        {row.name}
                      </Link>
                      <p className="font-mono text-xs text-slate-500">
                        {row.code}
                        {row.tin ? ` · ${row.tin}` : ''}
                      </p>
                    </Td>
                    <Td>
                      <div className="flex flex-wrap items-center gap-1">
                        <span>{row.category?.label ?? '—'}</span>
                        {row.isGovernment && <StatusChip tone="info">Government</StatusChip>}
                      </div>
                      {row.industry && (
                        <p className="text-xs text-slate-500">{row.industry.label}</p>
                      )}
                    </Td>
                    <Td>{[row.city, row.province].filter(Boolean).join(', ') || '—'}</Td>
                    <Td>
                      {row.contacts[0] ? (
                        <>
                          <p>{row.contacts[0].name}</p>
                          <p className="text-xs text-slate-500">{row.contacts[0].mobile ?? ''}</p>
                        </>
                      ) : (
                        <span className="text-slate-400">No contact yet</span>
                      )}
                    </Td>
                    <Td align="right" className="tabular">
                      {row._count.leads}
                    </Td>
                    <Td align="right">
                      <StatusChip tone={row.status === 'ACTIVE' ? 'ok' : 'neutral'}>
                        {row.status === 'ACTIVE' ? 'Active' : 'Disabled'}
                      </StatusChip>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>

            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span className="inline-flex items-center gap-2">
                <Building2 aria-hidden="true" className="h-4 w-4" />
                {total} customer{total === 1 ? '' : 's'}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((value) => value - 1)}
                >
                  Previous
                </Button>
                <span className="self-center">
                  {page} / {lastPage}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page >= lastPage}
                  onClick={() => setPage((value) => value + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
